import { Venue } from '../types';

const VENUE_NAME_STOP_WORDS = new Set([
  'the', 'and', 'grand', 'royal', 'banquet', 'banquets', 'hall', 'halls',
  'marquee', 'marquees', 'lawn', 'lawns', 'palace', 'garden', 'gardens',
  'hotel', 'ballroom', 'center', 'centre', 'club', 'karachi', 'event', 'events', 'complex', 'convention'
]);

/**
 * Resolves the venue ID that should receive the live voice spotlight aura
 * based on explicit server cue or natural spoken transcript matching (full name,
 * 2-word prefix, or distinctive single-word brand token).
 */
export function resolveVoiceSpotlightVenueId(
  spokenCueText: string,
  venues: Array<Pick<Venue, 'id' | 'name'>>,
  explicitVenueId?: string | null
): string | null {
  if (explicitVenueId) return explicitVenueId;
  if (!spokenCueText || !spokenCueText.trim() || !Array.isArray(venues) || venues.length === 0) {
    return null;
  }

  const lowerCue = spokenCueText.toLowerCase();
  let bestVenueId: string | null = null;
  let latestPos = -1;

  for (const v of venues) {
    if (!v?.name) continue;
    const lowerName = v.name.toLowerCase();

    // 1. Full venue name match
    const fullIdx = lowerCue.lastIndexOf(lowerName);
    if (fullIdx > latestPos) {
      latestPos = fullIdx;
      bestVenueId = v.id;
      continue;
    }

    // 2. Distinctive two-word prefix match (e.g. "Regent Plaza", "PC Marquee", "Gulshan Bagh")
    const words = lowerName.replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(Boolean);
    const prefix = words.slice(0, 2).join(' ');
    if (prefix.length >= 5) {
      const prefixIdx = lowerCue.lastIndexOf(prefix);
      if (prefixIdx > latestPos) {
        latestPos = prefixIdx;
        bestVenueId = v.id;
        continue;
      }
    }

    // 3. Distinctive single-word brand token (>= 4 chars, not a generic stop word, e.g. "Maritime", "Casablanca", "Majestic", "Opal")
    for (const token of words) {
      if (token.length >= 4 && !VENUE_NAME_STOP_WORDS.has(token)) {
        const tokenIdx = lowerCue.lastIndexOf(token);
        if (tokenIdx > latestPos) {
          latestPos = tokenIdx;
          bestVenueId = v.id;
        }
      }
    }
  }

  return bestVenueId;
}
